import {
  Grid,
  Stack,
  Typography,
  TablePagination,
  useTheme,
  Chip
} from '@mui/material';
import { SourcingExternalCandidateCard } from 'modules/sourcing-external/components/SourcingExternalCandidateCard';
import { SourcingExternalSearchFilter } from 'modules/sourcing-external/components/SourcingExternalSearchFilter';
import { SourcingLoadingIndicator } from 'common/animations/SourcingLoadingIndicator';
import { useSearchPeopleQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useSourcingExternalStore } from 'modules/sourcing-external/stores';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SourcingExternalSearchSelectionBar } from 'modules/sourcing-external/components/SourcingExternalSearchSelectionBar';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { SearchPeopleProfile } from 'modules/sourcing-external/types/SearchPeopleQueryItem';
import { SourcingExternalSearchDrawer } from 'modules/sourcing-external/components/SourcingExternalSearchDrawer';
import { SourcingExternalImproveFilterWarning } from 'modules/sourcing-external/components/SourcingExternalImproveFilterWarning';
import { ArrowForward } from '@mui/icons-material';
import { useEffect } from 'react';
import { SourcingExternalError } from 'modules/sourcing-external/components/SourcingExternalError';
import { SOURCING_EXTERNAL_MAX_CANDIDATES_SHOWN } from 'modules/sourcing-external/constants/sourcingExternalMaxCandidatesShown';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

const rowsPerPage = 25; // Set by ContactOut, cannot be changed
const maxPages = SOURCING_EXTERNAL_MAX_CANDIDATES_SHOWN / rowsPerPage;

export const SourcingExternalSearchStep = () => {
  const candidateBatch = useSourcingExternalStore(
    (state) => state.candidateBatch
  );
  const searchQueryInput = useSourcingExternalStore(
    (state) => state.searchQueryInput
  );
  const updateSearchQueryId = useSourcingExternalStore(
    (state) => state.updateSearchQueryId
  );
  const onChangePage = useSourcingExternalStore((state) => state.onChangePage);
  const page = searchQueryInput?.page ?? 1;

  // NOTE: This a bug on ContactOut
  // For some reason only for these value (years_in_current_role and years_of_experience) they don't accept an empty array
  const yearsInCurrentRole =
    searchQueryInput?.years_in_current_role?.length &&
    searchQueryInput?.years_in_current_role?.length > 0
      ? searchQueryInput.years_in_current_role
      : null;

  const yearsOfExperience =
    searchQueryInput?.years_of_experience?.length &&
    searchQueryInput?.years_of_experience?.length > 0
      ? searchQueryInput.years_of_experience
      : null;

  const { data, loading, error } = useSearchPeopleQuery({
    variables: {
      input: {
        ...searchQueryInput,
        years_in_current_role: yearsInCurrentRole,
        years_of_experience: yearsOfExperience,
        candidateBatchId: candidateBatch?.id as string
      }
    },
    skip: !searchQueryInput || !candidateBatch?.id
  });

  useEffect(() => {
    if (!searchQueryInput?.id || !data?.searchPeople?.id) return;
    updateSearchQueryId(data.searchPeople.id);
  }, [data]);

  const { palette } = useTheme();
  const { openDrawer } = useDrawerV2();

  const rows = data?.searchPeople?.profiles ?? [];
  const total = data?.searchPeople?.total ?? 0;

  const handleCardClick = (row: SearchPeopleProfile, index: number) => {
    const nextRow = rows[index + 1];
    const prevRow = rows[index - 1];
    openDrawer({
      width: '500px',
      title: row.fullName ?? '',
      subtitle: row.location ?? '',
      children: (
        <SourcingExternalSearchDrawer
          row={row}
          currentIndex={index}
          total={rows.length}
          onNext={
            nextRow ? () => handleCardClick(nextRow, index + 1) : undefined
          }
          onPrev={
            prevRow ? () => handleCardClick(prevRow, index - 1) : undefined
          }
        />
      )
    });
  };

  if (!loading && error) {
    return <SourcingExternalError />;
  }

  if (loading || !searchQueryInput) {
    return (
      <SourcingLoadingIndicator title="Finding suitable candidates from Popp's database." />
    );
  }

  return (
    <Stack>
      <Header
        title={`${new Intl.NumberFormat('en-US').format(
          total
        )} candidates found`}
        items={[
          <Stack
            key="job-description"
            direction="row"
            alignItems="center"
            gap={1}
          >
            <Typography>
              Based on your job description for{' '}
              <b>
                {getAnalysisTitle(candidateBatch)} in{' '}
                {candidateBatch?.jobDescriptionParsed?.location}
              </b>
            </Typography>
            <Chip
              label="BETA"
              variant="inverse"
              size="small"
              color="primary"
              sx={{ px: 1, cursor: 'pointer' }}
            />
          </Stack>
        ]}
        breadcrumbs={[
          { name: 'Analyse', path: Paths.ANALYSE },
          {
            name: getAnalysisTitle(candidateBatch),
            path: `${Paths.ANALYSE}/${candidateBatch?.id}`
          }
        ]}
        currentBreadcrumb="SOURCE CANDIDATES"
      />
      <Stack gap="34px" direction="row">
        <SourcingExternalSearchFilter />
        <Stack flex={1}>
          {rows.length === 0 && (
            <Stack
              height="100%"
              bgcolor="warningV2.light"
              py="60px"
              alignItems="center"
              gap="32px"
              flex={1}
            >
              <Stack gap="12px" alignItems="center">
                <Stack
                  sx={{
                    backgroundColor: 'warningV2.main',
                    borderRadius: '50%',
                    height: '56px',
                    width: '56px',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: 'warningV2.dark' }} />
                </Stack>
                <Typography variant="heading" color="warningV2.dark">
                  Nothing found
                </Typography>
              </Stack>
              <Typography color="warningV2.dark">
                Search for something else or adjust your filters.
              </Typography>
            </Stack>
          )}
          {rows.length > 0 && (
            <>
              <Grid container spacing="16px">
                {rows.map((row, index) => (
                  <Grid key={row.linkedInProfileUrl} item xs={4}>
                    <Stack sx={{ height: '100%' }}>
                      <SourcingExternalCandidateCard
                        row={row}
                        onCardClick={() => handleCardClick(row, index)}
                      />
                    </Stack>
                  </Grid>
                ))}
                {page !== maxPages && total / rowsPerPage > page && (
                  <Grid item xs={4}>
                    <Stack
                      sx={{ height: '100%', cursor: 'pointer' }}
                      onClick={() => onChangePage(page + 1)}
                    >
                      <Stack
                        sx={{
                          height: '100%',
                          p: '20px',
                          border: palette.border.small.grey.medium
                        }}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="heading"
                          sx={{
                            color: 'primary.main'
                          }}
                        >
                          Next 25
                        </Typography>
                        <ArrowForward
                          sx={{
                            color: 'primary.main'
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Grid>
                )}
                {total > SOURCING_EXTERNAL_MAX_CANDIDATES_SHOWN &&
                  page === maxPages && (
                    <Grid item xs={4}>
                      <Stack sx={{ height: '100%' }}>
                        <SourcingExternalImproveFilterWarning />
                      </Stack>
                    </Grid>
                  )}
              </Grid>
              <TablePagination
                component="div"
                count={
                  total > SOURCING_EXTERNAL_MAX_CANDIDATES_SHOWN
                    ? SOURCING_EXTERNAL_MAX_CANDIDATES_SHOWN
                    : total
                }
                page={page - 1}
                onPageChange={(_, newPage) => onChangePage(newPage + 1)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                nextIconButtonProps={{
                  disabled: page >= maxPages || total / rowsPerPage <= page
                }}
              />
            </>
          )}
        </Stack>
      </Stack>
      {candidateBatch && <SourcingExternalSearchSelectionBar rows={rows} />}
    </Stack>
  );
};
